import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { PublicResident } from "@/shared/contracts";
import { ErrorState, LoadingState } from "@/shared/ui";
import { photoWidthFor, usePhotoSizes } from "@/shared/ui/photoSizes";
import { publicIdentityBinding, usePublicIdentityMedia } from "../publicIdentityMedia";
import { agoText, nowLine, ORIGINAL_CHARACTER_TAG, presenceGroup } from "../residentView";
import { useDailyReturn } from "./DailySheet";
import { dailyStories, eligibleStoryResident, identityFaceCrop, scopeDaily, type DailyStories, type DailyStory } from "./guestStoriesModel";
import type { WorldFeed } from "./worldFeed";

/**
 * 「今天星球上发生的事」的内容（UI-WORLD-LIVING-117-1，用户 10-15 经 I：「先逛逛」落到全屏星球，列表退进上拉面板）。
 * 面板外壳（把手、收起拉起、记住开没开和滚到哪、Esc）在 ./DailySheet；这里只管里面列什么、怎么排：
 * - 按故事排（guestStoriesModel.dailyStories）：照片、相遇、活动、远行、TA 自己写的小事在前；
 *   什么都没有的居民只说此刻，排在后面；出门、回家这类例行的合成一句「N 位最近出过门」，展开才一条条看；
 * - 卡片整张就是「认识 TA」（不再每张挂「认识 TA →」、不再图片和文字两个停靠点）；形象照是暖底小方格，真实生活照整幅、写「生活片段」；
 * - 「形象照」在列表上面说一次；先说此刻（服务端 doing），再说那件事；
 * - 此刻多少位在家、在外面和人数按钮在面板把手上（./DailySheet），这里不重复；末尾只留一个去处「带我的宠物来」（登录在顶栏「加入星球」里）。
 * - 把手上那一句（最上面那件事）也由这里算好交上去（onPeek），和列表同一份：照片打不开当没有那一条时，两边一起改（评审 R2 第 8 条）。
 * - 城市层（scope）只说这一层那几位的事（UI-WORLD-LIVING-117-2，I 10-15：在哪一层说哪一层的今天）：排序在全星球上算，再留下和这几位有关的。
 * 不编内容：没有的事实就不显示；照片对不上、打不开就当没有那一条，不借别的图；形象照打不开的那张不给「认识 TA」。
 */

/** 标题下面那一行（UI-FIRST-IMPRESSION-116）：只按服务端 presence 分组数，不从 doing 文字里猜；一个都分不出来就不说。 */
export function presenceSummary(residents: readonly Pick<PublicResident, "presence">[]): string | null {
  let home = 0, away = 0;
  for (const resident of residents) {
    const group = presenceGroup(resident.presence);
    if (group === "home") home += 1;
    else if (group === "out" || group === "road") away += 1;
  }
  if (!home && !away) return null;
  if (!away) return home === residents.length ? "此刻都在家" : `此刻 ${home} 位在家`;
  return home ? `此刻 ${home} 位在家、${away} 位在外面` : `此刻 ${away} 位在外面`;
}

/** 近景格的取图边长：方格 CSS 宽 64–96，审过框的还要按框放大约两倍，统一取 384 档（photoWidthFor 大于 96 就是 384） */
const STORY_TILE_PX = 160;
/** 一开始列几张卡；多的收在「再看看」后面（不分页，已经读到的才排） */
const FIRST_STORIES = 6;

/** 小标题说这是哪一种事。照片卡图下已经写了「生活片段」、「在外面」和只说此刻的卡下面那行「此刻」已经说了，都不再挂；例行的不成卡、在那一句里 */
const TIER_CAPTION: Partial<Record<DailyStory["tier"], string>> = { met: "碰见了", event: "一起出发", trip: "出远门", post: "TA 写的" };

/** 一位居民的形象照：官方形象经访客那条路取（不借成员图、不借私图）。enabled=false 时不取（照片卡不画主角的形象照） */
function useFaceMedia(resident: PublicResident, enabled = true) {
  const binding = publicIdentityBinding({ petId: resident.pet_id, identity: resident.identity }, resident.avatar_url, resident.official_avatar === true);
  const sized = usePhotoSizes();
  const media = usePublicIdentityMedia(enabled ? binding : null, sized ? photoWidthFor(STORY_TILE_PX) : null);
  return { media, failed: !binding || media.failed };
}
type FaceMedia = ReturnType<typeof useFaceMedia>;

/** 形象照近景格：审过框的、图读出来宽高比对得上才按框放大。取图状态由卡片给（主角的失败要在同一次渲染里撤掉「认识 TA」） */
function Face({ resident, lead, face }: { resident: PublicResident; lead: boolean; face: FaceMedia }) {
  const { media, failed } = face;
  const imageRef = useRef<HTMLImageElement>(null);
  const [geometry, setGeometry] = useState<{ src: string; aspect: number } | null>(null);
  const cropHint = identityFaceCrop(resident);
  const crop = cropHint && geometry?.src === media.src && Math.abs(geometry.aspect - 2 / 3) < .001 ? cropHint : null;
  return <span className={`ps-discovery-story__visual${crop ? " has-face-focus" : ""}${lead ? " is-lead" : ""}`} data-face-focus={crop ? resident.pet_id + ":o1" : undefined}>
    {media.src && !failed ? <img ref={imageRef} key={media.src} src={media.src} alt={`${resident.name}的公开形象`}
      style={crop ? { width: `${100 / crop.width}%`, height: `${100 / crop.height}%`, maxWidth: "none", transform: `translate(${-crop.x * 100}%, ${-crop.y * 100}%)` } : undefined}
      loading={lead ? "eager" : "lazy"} draggable={false} onLoad={event => {
        if (media.src && event.currentTarget === imageRef.current && event.currentTarget.isConnected && event.currentTarget.naturalHeight > 0) setGeometry({ src: media.src, aspect: event.currentTarget.naturalWidth / event.currentTarget.naturalHeight });
      }} onError={event => { if (event.currentTarget === imageRef.current && event.currentTarget.isConnected) media.fail(); }} />
      : <span className="ps-discovery-story__image-state" role="status"><span className="visually-hidden">{failed ? "这张公开图片暂时看不到" : "正在打开公开形象…"}</span></span>}
  </span>;
}
/** 相遇卡上叠着的第二位 */
function OtherFace({ resident }: { resident: PublicResident }) {
  return <Face resident={resident} lead={false} face={useFaceMedia(resident)} />;
}

function Story({ story, lead, nowMs, quiet = false, onPhotoFail }: { story: DailyStory; lead: boolean; nowMs: number; quiet?: boolean; onPhotoFail: (url: string) => void }) {
  const [main, ...others] = story.residents;
  const photoKey = story.photo ? JSON.stringify([story.key, story.photo.url, story.photo.petId, story.photo.captured_at, story.photo.source_event_id]) : "";
  const photo = story.photo;
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const photoButtonRef = useRef<HTMLButtonElement>(null);
  const preview = Boolean(photo && previewKey === photoKey);
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!preview || !dialog) return;
    const trigger = photoButtonRef.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    return () => {
      const heldFocus = dialog.contains(document.activeElement) || document.activeElement === document.body;
      if (dialog.open) dialog.close();
      document.body.style.overflow = overflow;
      if (heldFocus) {
        // React may still have the withdrawn trigger connected during layout cleanup.
        // After this commit, restore only lost/modal focus, never an external choice.
        queueMicrotask(() => {
          const active = document.activeElement;
          if (active !== document.body && active !== trigger && !dialog.contains(active)) return;
          if (trigger?.isConnected && !trigger.disabled) trigger.focus({ preventScroll: true });
          else document.querySelector<HTMLButtonElement>(".ps-daily-sheet__handle")?.focus({ preventScroll: true });
        });
      }
    };
  }, [preview, photoKey]);
  // 主角的形象照：照片卡不取；打不开的那一张不给「认识 TA」——和失败同一次渲染撤掉，不多露一帧（父侧也是这样同步算的）
  const mainFace = useFaceMedia(main, !story.photo);
  const [expanded, setExpanded] = useState(() => { try { return sessionStorage.getItem(`petsoul:guest-story-expanded:${story.key}`) === "1"; } catch { return false; } });
  useEffect(() => { try { sessionStorage.setItem(`petsoul:guest-story-expanded:${story.key}`, expanded ? "1" : "0"); } catch { /* presentation only */ } }, [story.key, expanded]);
  const textId = useId();
  const now = nowLine(main);
  const names = story.residents.map(r => r.name).join("和");
  // 后端那句以名字开头（「墨白到了碑林区一带」「云朵和布丁遇见了」）：名字和那句连成一行标题，不再上面一个名字、下面又一个名字（R1 第 3 条）。
  // 照片里有好几位、那句只以照片主人开头（「B分享了一张出游照片」）：标题就是主人这一句，别的几位在照片里
  const head = story.event ? (story.text.startsWith(names) ? names : story.text.startsWith(main.name) ? main.name : null) : null;
  const rest = head !== null ? story.text.slice(head.length) : null;
  const showText = rest === null && story.text.trim() !== now;
  const long = story.text.length > 100;
  const detailPath = `/world/residents/${encodeURIComponent(main.pet_id)}?view=guest`;
  // 照片打不开、或主角的形象照打不开：这一张不给「认识 TA」，也不借别的图（Codex UI 115 的安全规矩照旧）
  const available = photo ? true : !mainFace.failed;
  const area = story.area && !story.text.includes(story.area) ? story.area : null;
  const when = (showText || rest !== null) && story.at ? story.at : null;
  return <article className={`ps-discovery-story ps-daily-story${lead ? " is-lead" : ""}`} data-story={story.key} data-tier={story.tier} data-image-role={photo ? "event" : "identity"}>
    {photo ? null : <div className={`ps-daily-story__faces${others.length ? " is-pair" : ""}`}>
      <Face resident={main} lead={lead} face={mainFace} />
      {others.slice(0, 1).map(r => <OtherFace key={r.pet_id} resident={r} />)}
    </div>}
    <div className="ps-discovery-story__words">
      {quiet || !TIER_CAPTION[story.tier] ? null : <p className="ps-daily-story__kicker">{TIER_CAPTION[story.tier]}</p>}
      <h3>{available ? <Link className="ps-daily-story__link" to={detailPath} aria-label={`认识 TA：${main.name}`}>{head ?? names}</Link> : <span>{head ?? names}</span>}
        {rest !== null ? <span className="ps-daily-story__rest">{rest}</span> : null}
        {main.resident_kind === "public_npc" ? <small>{ORIGINAL_CHARACTER_TAG}</small> : null}</h3>
      {story.tier === "met" ? null : <p className="ps-discovery-story__now" data-presence={presenceGroup(main.presence)}><span className="ps-discovery-story__now-label">此刻</span>{now}</p>}
      {showText ? <p id={textId} className={long && !expanded ? "ps-discovery-story__text is-folded" : "ps-discovery-story__text"}>{story.text}</p> : null}
      {showText && long ? <button type="button" className="ps-discovery-text-button" aria-expanded={expanded} aria-controls={textId} onClick={() => setExpanded(v => !v)}>{expanded ? "收起这段小事" : "展开这段小事"}</button> : null}
      {/* 那件事是什么时候的：放在地点后面（「在外面」是此刻，时间说的是那件事，不挨着「在外面」） */}
      <p className="ps-discovery-story__meta">{area ? <span>{area}</span> : null}{when ? <time dateTime={when}>{photo ? "发布于 " : ""}{agoText(when, nowMs)}</time> : null}{photo?.captured_at ? <time dateTime={photo.captured_at}>拍摄于 {agoText(photo.captured_at, nowMs)}</time> : null}</p>
    </div>
    {/* 生活照卡先说是谁、什么时候，照片在下面：DOM 里也排在后面，Tab、读屏和眼睛看到的同一个顺序（评审 R4 中 1、R5 中 2） */}
    {photo ? <figure className="is-event-photo">
      <div className="ps-discovery-story__visual">
        {/* 收着（面板 hidden）时也先读：打不开的早点当没有，卡片和把手那一句一起改，不等拉起来才跳（评审 R2 第 8 条） */}
        <img key={photoKey} src={photo.url} alt={photo.alt} loading="eager" draggable={false} onError={() => { setPreviewKey(null); onPhotoFail(photo.url); }} />
        <button ref={photoButtonRef} type="button" className="ps-discovery-story__photo-button" aria-label={`放大查看${main.name}的生活照片`} onClick={() => setPreviewKey(photoKey)} />
      </div>
      <figcaption>生活片段</figcaption>
    </figure> : null}
    {preview && photo ? <dialog ref={dialogRef} className="ps-discovery-photo-preview" aria-label={`${main.name}的完整生活照片`} onCancel={event => { event.preventDefault(); setPreviewKey(null); }}>
      <div className="ps-discovery-photo-preview__bar"><span>{main.name}的生活片段</span><button type="button" autoFocus aria-label="关上照片" onClick={() => setPreviewKey(null)}>×</button></div>
      <img key={photoKey} src={photo.url} alt={photo.alt} draggable={false} onError={() => { setPreviewKey(null); onPhotoFail(photo.url); }} />
      <p>{photo.alt}</p>
    </dialog> : null}
  </article>;
}

const ROUTINE_STORE = "petsoul:public-world:guest-daily-routine:v1";
const MORE_STORE = "petsoul:public-world:guest-daily-more:v1";
const readFlag = (key: string) => { try { return sessionStorage.getItem(key) === "1"; } catch { return false; } };
const saveFlag = (key: string, on: boolean) => { try { sessionStorage.setItem(key, on ? "1" : "0"); } catch { /* presentation only */ } };

/** 例行出门合成的一句：展开才一条条看，每条整行是「认识 TA」；从面板点进居民主页再回来还是展开的（R1 第 2 条），新进来一律收着（R2 第 5 条）。
    名单读得慢、这一句晚挂上（冷缓存）也照面板给的「这次是回来的」恢复（R3 挑刺） */
function RoutineGroup({ routine, nowMs }: { routine: readonly DailyStory[]; nowMs: number }) {
  const { returning } = useDailyReturn();
  const [open, setOpen] = useState(() => returning && readFlag(ROUTINE_STORE));
  useEffect(() => saveFlag(ROUTINE_STORE, open), [open]);
  const listId = useId();
  if (!routine.length) return null;
  return <section className="ps-daily-routine" aria-label="例行出门">
    <button type="button" className="ps-daily-routine__toggle" aria-expanded={open} aria-controls={listId} onClick={() => setOpen(v => !v)}>
      <span>{`${routine.length} 位最近出过门`}</span><span className="ps-daily-routine__hint"><span className="visually-hidden">，</span>{open ? "收起" : "看看是谁"}</span><span className={`ps-daily-routine__chev${open ? " is-open" : ""}`} aria-hidden="true" />
    </button>
    {open ? <ul id={listId} className="ps-daily-routine__list">{routine.map(story => {
      const who = story.residents[0];
      return <li key={story.key} data-story={story.key}><Link to={`/world/residents/${encodeURIComponent(who.pet_id)}?view=guest`}>
        <span className="visually-hidden">认识 TA：</span><span className="ps-daily-routine__name">{who.name}</span><span className="ps-daily-routine__text">{story.text}</span>{story.at ? <time dateTime={story.at}>{agoText(story.at, nowMs)}</time> : null}
      </Link></li>;
    })}</ul> : null}
  </section>;
}

/** 什么都没有的居民（没有动态、没写小事）：只说此刻，排在故事卡后面（不是空状态：TA 们就在星球上） */
function nowStory(resident: PublicResident): DailyStory {
  return { key: `resident:${resident.pet_id}`, tier: "routine", residents: [resident], text: nowLine(resident), at: null, photo: null, area: resident.city ? `${resident.city}居民` : null, event: null };
}

/** 把手上那一句：列表最上面那件事；没有就是此刻（GuestWorldStories 用自己那一份 dailyStories 算好，经 onPeek 交给 DailySheet） */
export function dailyPeek(daily: DailyStories): string | null {
  const first = daily.stories[0] ?? daily.routine[0] ?? (daily.quiet[0] ? nowStory(daily.quiet[0]) : null);
  if (!first) return null;
  return first.text.startsWith(first.residents[0].name) ? first.text : `${first.residents.map(r => r.name).join("和")}：${first.text}`;
}

export function GuestWorldStories({ residents, deniedIds, feed, feedPending, feedFailed, onRetryFeed, pending, error, onRetryWorld, canRegister, nowMs, onPeek, scope = null }: {
  residents: readonly PublicResident[]; deniedIds: ReadonlySet<string>; feed: WorldFeed | undefined; feedPending: boolean; feedFailed: boolean; onRetryFeed: () => void;
  pending: boolean; error: unknown; onRetryWorld: () => void; canRegister: boolean; nowMs: number;
  /** 把手上那一句（最上面那件事）；名单在读、读不出来时是 null */
  onPeek?: (peek: string | null) => void;
  /** 城市层：这一层看得见的那几位（PublicWorldMap 的 regionFocus）；星球层为 null */
  scope?: ReadonlySet<string> | null;
}) {
  const page = !feedFailed && feed?.layer_ready !== false ? feed : undefined;
  const [accepted, setAccepted] = useState<string[] | null>(() => {
    try { const value: unknown = JSON.parse(sessionStorage.getItem("petsoul:guest-story-order:v1") || "null"); return Array.isArray(value) && value.length <= 50 && value.every(v => typeof v === "string") ? value : null; } catch { return null; }
  });
  useEffect(() => { if (accepted) try { sessionStorage.setItem("petsoul:guest-story-order:v1", JSON.stringify(accepted)); } catch { /* event IDs only */ } }, [accepted]);
  // The first success establishes the visible order once. Background refresh never jumps reading position.
  if (accepted === null && page) setAccepted(page.items.map(e => e.event_id));
  // 这次打开里读失败的照片地址：那一条当没有（R2 第 8 条）
  const [badPhotos, setBadPhotos] = useState<ReadonlySet<string>>(() => new Set());
  const all = !pending && !error ? dailyStories(residents, page?.items ?? [], accepted, deniedIds, badPhotos) : { stories: [], routine: [], quiet: [] };
  const daily = scope ? scopeDaily(all, scope) : all;
  const peek = !pending && !error ? dailyPeek(daily) : null;
  useEffect(() => { onPeek?.(peek); }, [onPeek, peek]);
  const onPhotoFail = (url: string) => setBadPhotos(bad => bad.has(url) ? bad : new Set(bad).add(url));
  const validIds = new Set(residents.filter(r => !deniedIds.has(r.pet_id) && eligibleStoryResident(r)).map(r => r.pet_id));
  const fresh = accepted && page ? page.items.filter(e => !accepted.includes(e.event_id) && e.pet_ids.some(p => validIds.has(p))).length : 0;
  const cards = [...daily.stories, ...daily.quiet.map(nowStory)];
  const back = useDailyReturn();
  const [more, setMore] = useState(() => back.returning && readFlag(MORE_STORE));
  useEffect(() => saveFlag(MORE_STORE, more), [more]);
  // 回来时内容齐了（名单读完、「再看看」「例行」都按记的展开）面板才滚回原处；冷缓存时挂上那一刻还是骨架（R3 挑刺）
  const ready = !pending && !error;
  useLayoutEffect(() => { if (ready) back.restore(); }, [ready, back]);
  const storiesRef = useRef<HTMLDivElement>(null);
  const [focusFrom, setFocusFrom] = useState<number | null>(null);
  useEffect(() => {
    if (focusFrom === null) return;
    setFocusFrom(null);
    // 按钮点完就没了（「再看看」「有新鲜事，点击更新」）：焦点给那一张卡的「认识 TA」，整张卡滚进视野（焦点框画在卡外圈，R2、R3 挑刺 12）；
    // 那张没有链接（形象照打不开）就回到面板把手，不掉到 body
    const card = storiesRef.current?.querySelectorAll<HTMLElement>("article[data-story]")[focusFrom];
    const link = card?.querySelector<HTMLElement>("a.ps-daily-story__link");
    if (link && card) { link.focus({ preventScroll: true }); card.scrollIntoView?.({ block: "nearest" }); }
    else document.querySelector<HTMLElement>(".ps-daily-sheet__handle")?.focus({ preventScroll: true });
  }, [focusFrom]);
  const shown = more ? cards : cards.slice(0, FIRST_STORIES);
  const hidden = cards.length - shown.length;
  const firstFace = shown.findIndex(story => !story.photo);
  const portraitNote = <p className="ps-discovery-portrait-note">头像是居民的形象照，不是当时的现场照片。</p>;
  const anything = cards.length + daily.routine.length > 0;
  return <div className="ps-discovery-content">
    {pending ? <LoadingState lines={3} label="正在看看星球上的今天…" /> : error ? <ErrorState error={error} onRetry={onRetryWorld} /> : anything ? <>
      {fresh ? <button type="button" className="ps-discovery-fresh" onClick={() => { setAccepted(page!.items.map(e => e.event_id)); setFocusFrom(0); }}>有新鲜事，点击更新</button> : null}
      {/* 「头像是形象照」说在第一张有头像的卡上面（例行的那一句里没有头像，不算）：头条是真生活照时，不贴着照片说「不是现场照片」（评审 R6 挑刺） */}
      {firstFace === 0 ? portraitNote : null}
      <div ref={storiesRef} className="ps-discovery-stories">{shown.map((story, index) => <Fragment key={story.key}>
        {index === firstFace && index > 0 ? portraitNote : null}
        <Story story={story} lead={index === 0} nowMs={nowMs} quiet={story.key.startsWith("resident:")} onPhotoFail={onPhotoFail} />
      </Fragment>)}</div>
      {hidden > 0 ? <button type="button" className="ps-daily-more" onClick={() => { setMore(true); setFocusFrom(shown.length); }}><span>{`再看看 ${hidden} 位`}</span><span className="ps-daily-more__chev" aria-hidden="true" /></button> : null}
      <RoutineGroup routine={daily.routine} nowMs={nowMs} />
      {canRegister ? <section className="ps-discovery-invite" aria-label="加入星球"><p>也想在这里留下你和 TA 的日常？</p><Link className="ps-discovery-join" to="/register">带我的宠物来</Link></section> : null}
    </> : <section className="ps-discovery-empty" role="status"><h3>这里暂时还没有可以见面的居民</h3><p>有可以公开认识的伙伴时，会出现在这里。</p>{canRegister ? <Link to="/register">带我的宠物来</Link> : null}</section>}
    {feedPending ? <p className="ps-discovery-feed-note" role="status">新鲜事还在读，先看看居民们的公开近况。</p> : feedFailed || feed?.layer_ready === false ? <div className="ps-discovery-feed-note" role="status"><p>这次没有读到公开新鲜事，当前近况来自居民资料。</p><button type="button" className="ps-discovery-text-button" onClick={onRetryFeed}>再读一次新鲜事</button></div> : null}
  </div>;
}
